import React, { useCallback, useEffect, useRef, useState } from 'react'

import { ContentState, EditorState, convertToRaw } from 'draft-js'
import draftToHtml from 'draftjs-to-html'
import { ErrorMessage, FieldHookConfig, useField } from 'formik'

import CustomTextEditorField from '@/components/CustomFields/components/CustomTextEditor'
import CustomTextEditorMention from '@/components/CustomFields/components/CustomTextEditorMention'
import { isJSON } from '@/utils'

export type TextEditorFieldProps = {
  name: string
  setFieldValue: (name: string, newValue: string) => void
  onChangeApplied?: VoidFunction
  onMention?: (user: any) => void
  allowMentions?: boolean
  label?: string
  parentClassName?: string
  previewClassName?: string
  placeholder?: string
  disabled?: boolean
  readOnly?: boolean
  defaultShowEditor?: boolean
  onCancel?: any
  cancelLabel?: string
  saveLabel?: string
  showSaveButton?: boolean
} & FieldHookConfig<string>

/**
 * @deprecated use Lexical
 * @see Streaming Notes for an example of Lexical usage
 */
const TextEditorField = (props: TextEditorFieldProps) => {
  const {
    setFieldValue,
    onChangeApplied,
    onMention,
    disabled,
    readOnly,
    label,
    parentClassName,
    previewClassName,
    allowMentions = false,
    placeholder = '~',
    defaultShowEditor,
    onCancel,
    cancelLabel = 'Cancel',
    saveLabel = 'Save',
    showSaveButton = true,
    ...fieldHookConfig
  } = props
  const [field] = useField(fieldHookConfig)
  const [showEditor, setShowEditor] = useState(false)
  const isEditor = defaultShowEditor ? defaultShowEditor : showEditor
  const lastSavedContent = useRef<any>(null)

  field.value = field.value ?? ''

  const prepareDraft = (value: string) => {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const htmlToDraft = require('html-to-draftjs').default
    const draft = htmlToDraft(value)
    const contentState = ContentState.createFromBlockArray(draft.contentBlocks)
    const editorState = EditorState.createWithContent(contentState)

    return EditorState.moveSelectionToEnd(editorState)
  }

  const [editorState, setEditorState] = useState(EditorState.createEmpty())

  useEffect(() => {
    if (!field.value) {
      setEditorState(EditorState.createEmpty())
      return
    }

    if (field.value !== draftToHtml(convertToRaw(editorState.getCurrentContent()))) {
      setEditorState(prepareDraft(field.value))
    }
  }, [field.value])

  const isEmpty = (editorState) => {
    return (
      convertToRaw(editorState.getCurrentContent()).blocks.length === 1 &&
      convertToRaw(editorState.getCurrentContent()).blocks[0].text === ''
    )
  }

  const saveToForm = (editorName: string, editorState: EditorState) => {
    let forFormik = draftToHtml(convertToRaw(editorState.getCurrentContent()))

    // Check if empty
    if (isEmpty(editorState)) {
      forFormik = ''

      if (fieldHookConfig.required) {
        return
      }
    }

    lastSavedContent.current = forFormik
    setFieldValue(editorName, forFormik)
    setShowEditor(false)

    if (onChangeApplied) {
      setTimeout(() => onChangeApplied(), 0)
    }
  }

  const cancelForm = () => {
    setEditorState(prepareDraft(lastSavedContent.current || field.value))
    setShowEditor(false)
    if (onCancel) onCancel()
  }

  const getRestStateText = (value: string) => {
    if (isJSON(value)) {
      return draftToHtml(JSON.parse(value))
    } else if (!value || value.startsWith('<p></p>')) {
      return placeholder
    }

    return value
  }

  const getStateValue = (value: string) => {
    if (isJSON(value)) {
      return draftToHtml(JSON.parse(value))
    } else if (value.startsWith('<p></p>')) {
      return ''
    }

    return value
  }

  return (
    <div className={`${parentClassName || ''} text-editor relative`}>
      {disabled && (
        <div className='absolute top-0 left-0 right-0 bottom-0 z-10 bg-white opacity-50 cursor-default' />
      )}
      {!!label && (
        <label
          htmlFor={fieldHookConfig.name}
          className='text-sm text-gray-400 block mb-2'
        >
          {label}
        </label>
      )}
      {isEditor ? (
        <>
          {allowMentions ? (
            <>
              <CustomTextEditorMention
                onChange={(editorName, editorState: EditorState) => {
                  setEditorState(editorState)
                  if (!showSaveButton) saveToForm(editorName, editorState)
                }}
                editorName={fieldHookConfig.name}
                editorState={editorState}
                placeholder={placeholder}
                onMention={onMention}
              />
            </>
          ) : (
            <CustomTextEditorField
              onChange={(editorName, editorState: EditorState) => {
                setEditorState(editorState)
                if (!showSaveButton) saveToForm(editorName, editorState)
              }}
              editorName={fieldHookConfig.name}
              editorState={editorState}
              placeholder={placeholder}
            />
          )}
          <div className='flex items-center justify-end gap-3 my-3'>
            {!defaultShowEditor && (
              <button
                type='button'
                className='btn btn-xs btn-ghost'
                onClick={cancelForm}
              >
                {cancelLabel}
              </button>
            )}
            {showSaveButton && (
              <button
                type='button'
                className='btn btn-xs btn-primary'
                onClick={() => saveToForm(fieldHookConfig.name, editorState)}
              >
                {saveLabel}
              </button>
            )}
          </div>
        </>
      ) : (
        <div
          className={`prose-sm prose-ul:list-disc prose-ol:list-decimal prose-li:-m-0 p-1 ${
            readOnly ? 'cursor-default' : 'hover:cursor-text hover:bg-gray-50'
          } ${previewClassName || ''}`}
          onClick={() => !readOnly && setShowEditor(true)}
          dangerouslySetInnerHTML={{
            __html: getRestStateText(field.value),
          }}
        />
      )}

      <input
        name={fieldHookConfig.name}
        type='hidden'
        data-test={fieldHookConfig['data-test']}
        value={getStateValue(field.value)}
      />

      <ErrorMessage
        component='span'
        name={fieldHookConfig.name}
        className='text-error'
      />
    </div>
  )
}

export default TextEditorField
